import { type FC, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Spinner } from '../../../components/Spinner/Spinner'
import { NavigationSidebar } from '../../../components/NavigationWithSideBar/NavigationSidebar'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import {
  useGetAccountSchedulingSettingsQuery,
  useGetUserSchedulingSettingsQuery,
  useResetUserSchedulingSettingsMutation,
  useUpdateUserSchedulingSettingsMutation,
} from '../../../store/scheduling/scheduling'
import { type SettingsResetScope, type UserSchedulingSettings } from '../../../models/Scheduling'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { YourEventTypesSection } from './sections/YourEventTypesSection'
import { YourBookingPageSection } from './sections/YourBookingPageSection'
import { SchedulingIntegrationsSection } from './sections/SchedulingIntegrationsSection'
import { YourDefaultsSection } from './sections/YourDefaultsSection'
import { BookingRulesSection } from './sections/BookingRulesSection'

export type UpdateUserSettings = (update: Partial<UserSchedulingSettings>) => Promise<void>

const NAV_SECTIONS = [
  { id: 'event-types', label: 'Your event types' },
  { id: 'booking-page', label: 'Your public booking page' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'availability', label: 'Availability' },
  { id: 'booking-rules', label: 'Booking rules' },
]

export const SchedulingProfileTab: FC = () => {
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const { data: userSettings, isLoading, isFetching } = useGetUserSchedulingSettingsQuery({ accountId, userId: authenticatedUser.id })
  const { data: accountSettings, isLoading: isAccountSettingsLoading } = useGetAccountSchedulingSettingsQuery({ accountId })
  const [updateUserSettings, { isLoading: isUpdating }] = useUpdateUserSchedulingSettingsMutation()
  const [resetUserSettings, { isLoading: isResetting }] = useResetUserSchedulingSettingsMutation()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSectionId = NAV_SECTIONS.find((section) => section.id === searchParams.get('section'))?.id ?? 'event-types'
  const isSaving = isUpdating || isResetting || (isFetching && !isLoading)

  const handleSectionClick = (sectionId: string) =>
    setSearchParams(
      (params) => {
        params.set('section', sectionId)
        return params
      },
      { replace: true },
    )

  const handleUpdate: UpdateUserSettings = async (update) => {
    const result = await updateUserSettings({ accountId, userId: authenticatedUser.id, update })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not save the setting', messages: ['Please try again.'] }))
    } else {
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_USER_SETTINGS_UPDATED, eventDetails: { fields: Object.keys(update) } })
    }
  }

  const handleResetToDefaults = async (scopes: SettingsResetScope[]) => {
    const result = await resetUserSettings({ accountId, userId: authenticatedUser.id, scopes })
    if ('error' in result) {
      dispatch(addToast({ variant: 'error', title: 'Could not reset your settings', messages: ['Please try again.'] }))
    } else {
      trackingClient.track({ eventName: TrackEvent.SCHEDULING_USER_SETTINGS_RESET, eventDetails: { scopes } })
    }
  }

  if (isLoading || isAccountSettingsLoading) {
    return (
      <div className='d-flex justify-content-center py-5'>
        <Spinner />
      </div>
    )
  }

  if (!userSettings || !accountSettings) {
    return (
      <div className='d-flex flex-column align-items-center text-center gap-1 py-5'>
        <span className='bold'>Could not load your scheduling settings</span>
        <span className='paragraph-small zocks-text-gray-600'>Please refresh the page to try again.</span>
      </div>
    )
  }

  const sectionContent: Record<string, ReactNode> = {
    'event-types': <YourEventTypesSection userSettings={userSettings} accountSettings={accountSettings} />,
    'booking-page': <YourBookingPageSection userSettings={userSettings} accountSettings={accountSettings} isSaving={isSaving} onUpdate={handleUpdate} />,
    integrations: <SchedulingIntegrationsSection userSettings={userSettings} isSaving={isSaving} onUpdate={handleUpdate} />,
    availability: (
      <YourDefaultsSection
        userSettings={userSettings}
        accountSettings={accountSettings}
        isSaving={isSaving}
        onUpdate={handleUpdate}
        onReset={() => handleResetToDefaults(['availability'])}
      />
    ),
    'booking-rules': (
      <BookingRulesSection
        userSettings={userSettings}
        accountSettings={accountSettings}
        isSaving={isSaving}
        onUpdate={handleUpdate}
        onReset={() => handleResetToDefaults(['booking_rules'])}
      />
    ),
  }

  return (
    <div className='scroll-navigation__container'>
      <div className='d-flex flex-column gap-4'>
        <NavigationSidebar sections={NAV_SECTIONS} activeSectionId={activeSectionId} onSectionClick={handleSectionClick} />
      </div>
      <div className='scroll-navigation__sections'>{sectionContent[activeSectionId]}</div>
    </div>
  )
}
